import { useNavigate } from 'react-router-dom';
import { products } from '@/mocks/homeData';
import { useScrollReveal, useScrollRevealMultiple } from '@/hooks/useScrollReveal';

export default function ProductShowcase() {
  const navigate = useNavigate();
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal();
  const { setRef, visible } = useScrollRevealMultiple(products.length);

  return (
    <section className="relative bg-background-100 py-10 md:py-20 overflow-hidden">
      {/* 背景裝飾光暈 */}
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-accent-500/8 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-primary-500/6 blur-2xl pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-secondary-500/5 blur-3xl pointer-events-none" />

      <div className="relative px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div
          ref={headerRef}
          className={`flex flex-col md:flex-row md:items-end md:justify-between mb-10 md:mb-14 scroll-reveal ${headerVisible ? 'scroll-reveal-visible' : ''}`}
        >
          <div className="max-w-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-sparkling-2-line text-accent-600 text-lg" />
                </div>
              </div>
              <span className="text-xs font-bold tracking-[0.2em] text-accent-600 uppercase">
                精選推薦
              </span>
            </div>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
              熱銷助聽器
              <span className="block text-base md:text-lg font-normal text-foreground-600 mt-2">
                專業選配，讓聆聽更清晰
              </span>
            </h2>
          </div>
          <button
            onClick={() => navigate('/products')}
            className="hidden md:inline-flex items-center gap-2.5 bg-primary-500 text-background-50 px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap group"
          >
            <span className="font-semibold">全系列產品</span>
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-arrow-right-line text-background-50 text-base group-hover:translate-x-0.5 transition-transform duration-300" />
            </div>
          </button>
        </div>

        {/* 品牌標語 */}
        <div className="mb-10 md:mb-14">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-accent-50 via-accent-100/60 to-accent-50 border border-accent-200/50">
            {/* 背景裝飾波形 */}
            <div className="absolute inset-0 opacity-[0.03]">
              <div className="absolute top-0 left-10 w-40 h-full border-l border-dashed border-accent-400" />
              <div className="absolute top-0 left-20 w-40 h-full border-l border-dashed border-accent-400" />
            </div>
            <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-accent-400/10 blur-2xl pointer-events-none" />
            <div className="absolute -bottom-8 -left-8 w-24 h-24 rounded-full bg-primary-400/8 blur-xl pointer-events-none" />

            <div className="relative flex flex-col md:flex-row items-center gap-5 md:gap-8 px-6 py-6 md:px-10 md:py-7">
              {/* 左側圖標區 */}
              <div className="flex-shrink-0">
                <div className="relative">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-accent-500 flex items-center justify-center shadow-sm">
                    <div className="w-9 h-9 md:w-11 md:h-11 flex items-center justify-center">
                      <i className="ri-voiceprint-line text-background-50 text-2xl md:text-4xl" />
                    </div>
                  </div>
                  {/* 圖標光暈 */}
                  <div className="absolute inset-0 rounded-2xl bg-accent-400 blur-md opacity-40 -z-10" />
                </div>
              </div>

              {/* 文字區 */}
              <div className="flex-1 text-center md:text-left">
                <div className="inline-flex items-center gap-2 mb-2">
                  <div className="w-6 h-px bg-accent-300 hidden md:block" />
                  <span className="text-xs font-bold tracking-[0.25em] text-accent-500 uppercase">
                    品牌宣言
                  </span>
                  <div className="w-6 h-px bg-accent-300 hidden md:block" />
                </div>
                <p className="text-lg md:text-2xl font-bold text-foreground-950 leading-snug">
                  讓我們成為您的
                  <span className="text-accent-600">智慧聽力顧問</span>
                </p>
                <p className="text-base md:text-lg font-medium text-foreground-600 mt-1.5 leading-relaxed">
                  照護您的聽力健康
                </p>
              </div>

              {/* 右側裝飾引號 */}
              <div className="hidden md:flex flex-shrink-0 items-start">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-double-quotes-r text-accent-300 text-5xl opacity-40" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Products grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7">
          {products.map((product, i) => {
            // Pick a highlight badge for higher priced items
            const badges: Record<number, { text: string; variant: 'accent' | 'primary' | 'secondary' }> = {
              1: { text: '隱形設計', variant: 'primary' },
              2: { text: '旗艦機種', variant: 'accent' },
              4: { text: '迷你輕巧', variant: 'secondary' },
              6: { text: '藍牙智能', variant: 'accent' },
            };
            const badge = badges[product.id];

            return (
              <div
                key={product.id}
                ref={setRef(i)}
                className={`group bg-white rounded-2xl border border-background-200 overflow-hidden cursor-pointer hover:border-accent-300/50 hover:shadow-lg transition-all duration-500 scroll-reveal scroll-reveal-delay-${Math.min(i + 1, 6)} ${visible[i] ? 'scroll-reveal-visible' : ''}`}
                onClick={() => navigate(`/product/${product.id}`)}
              >
                {/* 圖片區 */}
                <div className="relative w-full h-56 md:h-72 overflow-hidden bg-gradient-to-br from-background-100 to-background-200">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700 ease-out"
                  />

                  {/* 底部漸變疊層 */}
                  <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-primary-900/50 via-primary-900/15 to-transparent" />

                  {/* 左上角 badge */}
                  {badge && (
                    <div className="absolute top-4 left-4 z-10">
                      <span
                        className={`inline-block px-3.5 py-1.5 rounded-full text-xs font-bold tracking-wide shadow-sm backdrop-blur-sm ${
                          badge.variant === 'accent'
                            ? 'bg-accent-500/90 text-background-50'
                            : badge.variant === 'primary'
                              ? 'bg-primary-500/90 text-background-50'
                              : 'bg-secondary-500/90 text-background-50'
                        }`}
                      >
                        {badge.text}
                      </span>
                    </div>
                  )}

                  {/* Hover 中間查看圖示 */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
                    <div className="w-14 h-14 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-xl translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-eye-line text-primary-600 text-xl" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* 文字資訊區 */}
                <div className="p-5 md:p-6">
                  {/* 副標 */}
                  <span className="text-xs font-semibold text-accent-600 uppercase tracking-wider">
                    {product.subtitle}
                  </span>

                  {/* 產品名稱 */}
                  <h3 className="mt-1.5 text-lg font-bold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors duration-300">
                    {product.name}
                  </h3>

                  {/* 分隔線 */}
                  <div className="mt-4 mb-4 w-8 h-px bg-background-300 group-hover:w-16 group-hover:bg-accent-400 transition-all duration-500" />

                  {/* 價格 + CTA */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-baseline gap-1">
                      <span className="text-xs text-foreground-500 font-medium">NT$</span>
                      <span className="text-2xl font-bold text-foreground-950">
                        {product.price.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground-700 group-hover:text-accent-600 transition-colors duration-300">
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">查看詳情</span>
                      <div className="w-8 h-8 rounded-full bg-primary-100 group-hover:bg-primary-500 flex items-center justify-center transition-all duration-300">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-right-line text-primary-600 text-sm group-hover:text-background-50 transition-colors duration-300" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* 行動版按鈕 */}
        <div className="mt-8 md:hidden text-center">
          <button
            onClick={() => navigate('/products')}
            className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-3.5 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
          >
            全系列產品
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-line text-background-50" />
            </div>
          </button>
        </div>
      </div>
    </section>
  );
}